import React, { useState } from 'react';
import { Mail, MapPin, Instagram, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - in reality would send to dosboldlatinas@gmail.com
    toast({
      title: 'Message Sent!',
      description: 'Thank you for reaching out. We\'ll get back to you soon!',
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      detail: 'dosboldlatinas@gmail.com',
      link: 'mailto:dosboldlatinas@gmail.com',
    },
    {
      icon: Instagram,
      title: 'Social Media',
      detail: '@dosboldlatinas',
      link: 'https://www.instagram.com/dosboldlatinas/',
    },
    {
      icon: MapPin,
      title: 'Location',
      detail: 'Grand Rapids, MI',
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section 
        className="py-20 px-6 text-white relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Have questions, collaboration ideas, or just want to say hello? 
            We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="shadow-xl border-none">
              <CardHeader>
                <CardTitle className="text-3xl">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-base mb-2 block">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      required
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base mb-2 block">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-base mb-2 block">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      required
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base mb-2 block">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us what's on your mind..."
                      required
                      rows={6}
                      className="resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full text-lg py-6 transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#b76e79' }}
                  >
                    <Send className="mr-2" size={20} />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-4">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  For business inquiries, podcast guest opportunities, or just to connect - 
                  we're here to listen!
                </p>
              </div>

              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  const content = (
                    <Card 
                      className="hover:shadow-lg transition-all duration-300 border-l-4 cursor-pointer hover:-translate-y-1"
                      style={{ borderLeftColor: '#b76e79' }}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: '#b76e79' }}
                          >
                            <Icon size={24} className="text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 mb-1">{info.title}</p>
                            <p className="text-lg font-semibold">{info.detail}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );

                  return info.link ? (
                    <a key={index} href={info.link} target="_blank" rel="noopener noreferrer">
                      {content}
                    </a>
                  ) : (
                    <div key={index}>{content}</div>
                  );
                })}
              </div>

              {/* Social Media */}
              <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-none">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Follow Us</h3>
                  <p className="text-gray-300 mb-6">
                    Stay connected and join our community on social media @dosboldlatinas
                  </p>
                  <a 
                    href="https://www.instagram.com/dosboldlatinas/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="outline"
                      className="bg-white/10 text-white border-white/20 hover:bg-white hover:text-black"
                    >
                      <Instagram className="mr-2" size={20} />
                      @dosboldlatinas
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;